import { Link } from "react-router-dom";
import { Palette, Shield } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                <Shield className="w-6 h-6 text-violet-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Privacy Policy
              </h1>
            </div>
            
            <p className="text-slate-500 mb-8">Last updated: January 26, 2025</p>

            <div className="prose prose-slate max-w-none">
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">1. Introduction</h2>
                <p className="text-slate-600 leading-relaxed">
                  Welcome to ColorWonder AI ("we," "our," or "us"). We are committed to protecting your personal information 
                  and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                  information when you use our website and services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">2. Information We Collect</h2>
                <p className="text-slate-600 leading-relaxed mb-4">We collect information that you provide directly to us:</p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><strong>Account Information:</strong> Name, email address, and password when you create an account</li>
                  <li><strong>Payment Information:</strong> Payment card details processed securely through Stripe</li>
                  <li><strong>Usage Data:</strong> Information about how you use our services, including prompts and generated content</li>
                  <li><strong>Device Information:</strong> IP address, browser type, and device identifiers for security purposes</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">3. How We Use Your Information</h2>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>To provide and maintain our services</li>
                  <li>To process your transactions and send related information</li>
                  <li>To send you technical notices, updates, and support messages</li>
                  <li>To respond to your comments, questions, and customer service requests</li>
                  <li>To prevent fraud and abuse of our services</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">4. Payment Processing</h2>
                <p className="text-slate-600 leading-relaxed">
                  We use Stripe for payment processing. Your payment card details are collected and processed directly by Stripe 
                  and are not stored on our servers. Stripe's privacy policy can be found at{" "}
                  <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">
                    stripe.com/privacy
                  </a>.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">5. Data Security</h2>
                <p className="text-slate-600 leading-relaxed">
                  We implement appropriate technical and organizational security measures to protect your personal information. 
                  However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">6. Data Retention</h2>
                <p className="text-slate-600 leading-relaxed">
                  We retain your personal information for as long as your account is active or as needed to provide you services. 
                  We may retain certain information as required by law or for legitimate business purposes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">7. Your Rights</h2>
                <p className="text-slate-600 leading-relaxed mb-4">You have the right to:</p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Access your personal information</li>
                  <li>Correct inaccurate information</li>
                  <li>Delete your account and personal information</li>
                  <li>Object to processing of your personal information</li>
                  <li>Export your data in a portable format</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">8. Children's Privacy</h2>
                <p className="text-slate-600 leading-relaxed">
                  Our services are not intended for children under 13 years of age. We do not knowingly collect personal 
                  information from children under 13. If you are a parent or guardian and believe your child has provided 
                  us with personal information, please contact us.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">9. Changes to This Policy</h2>
                <p className="text-slate-600 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
                  Privacy Policy on this page and updating the "Last updated" date.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">10. Contact Us</h2>
                <p className="text-slate-600 leading-relaxed">
                  If you have any questions about this Privacy Policy, please contact us at:{" "}
                  <a href="mailto:privacy@colorwonder.app" className="text-violet-600 hover:underline">
                    privacy@colorwonder.app
                  </a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
